import { useEffect, useState } from "react";
import { durableGet, durableSet } from "../../durableStore";

// The signature lives in Supabase Storage and Settings only holds its web
// address, so a contract printed with no internet showed a broken image. A
// copy of the image itself is kept on this machine (electron-store on
// desktop, localStorage on the web) and the contract prints from that copy.
//
// Stored as { url, dataUrl } — one signature per machine (the signed-in
// user's). The address carries ?t=<upload time>, so a re-upload changes it and
// the copy is refreshed the next time the app is online. Wiped with the other
// du_ caches on sign-out / access removal (shared/accessWipe.ts).
export const SIGNATURE_IMAGE_KEY = "du_signature_image";

type Cached = { url: string; dataUrl: string };

function readCache(): Cached | null {
  try {
    const raw = durableGet(SIGNATURE_IMAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed.url === "string" && typeof parsed.dataUrl === "string"
      && parsed.dataUrl.startsWith("data:image/")
      ? parsed
      : null;
  } catch {
    return null;
  }
}

// Same file, ignoring the ?t= upload stamp.
const samePath = (a: string, b: string) => a.split("?")[0] === b.split("?")[0];

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

const inFlight = new Map<string, Promise<string | null>>();

/**
 * Download the signature and keep a copy on this machine. Safe to call often:
 * it does nothing when that exact address is already saved, and every failure
 * (offline, blocked) just leaves the existing copy in place.
 */
export function saveSignatureCopy(url: string | null | undefined): Promise<string | null> {
  if (!url || url.startsWith("data:")) return Promise.resolve(null);
  const cached = readCache();
  if (cached?.url === url) return Promise.resolve(cached.dataUrl);
  const pending = inFlight.get(url);
  if (pending) return pending;
  const job = (async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const blob = await res.blob();
      if (!blob.type.startsWith("image/")) return null;
      const dataUrl = await blobToDataUrl(blob);
      durableSet(SIGNATURE_IMAGE_KEY, JSON.stringify({ url, dataUrl }));
      return dataUrl;
    } catch {
      return null;
    } finally {
      inFlight.delete(url);
    }
  })();
  inFlight.set(url, job);
  return job;
}

/**
 * The image source to show for this signature address: the saved copy when it
 * is for this exact upload, otherwise the address (while a copy is fetched).
 * If the address fails to load (offline), fall back to a saved copy of the
 * same file, and failing that show nothing — never a broken-image icon.
 */
export function useSignatureSrc(url: string | null | undefined) {
  const initial = () => {
    if (!url) return null;
    const cached = readCache();
    return cached?.url === url ? cached.dataUrl : url;
  };
  const [src, setSrc] = useState<string | null>(initial);

  useEffect(() => {
    setSrc(initial());
    let alive = true;
    void saveSignatureCopy(url).then((dataUrl) => {
      if (alive && dataUrl) setSrc(dataUrl);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const onError = () => {
    const cached = readCache();
    if (url && cached && samePath(cached.url, url) && src !== cached.dataUrl) {
      setSrc(cached.dataUrl);
    } else {
      setSrc(null);
    }
  };

  return { src, onError };
}

export default function SignatureImage({
  url,
  alt = "Contractor signature",
  className,
}: {
  url: string | null | undefined;
  alt?: string;
  className?: string;
}) {
  const { src, onError } = useSignatureSrc(url);
  if (!src) return null;
  return <img src={src} alt={alt} className={className} onError={onError} />;
}
